import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { initAnalytics } from '../utils/analytics';
import { readConsent, saveConsent, GRANTED, DENIED } from '../utils/consent';
import '../styles/CookieBanner.css';

/*
  Asks once, then never again.

  Accept and Decline are given the same weight — same size, same
  colour, side by side. Making "Decline" a grey whisper next to a big
  gold "Accept" is the dark pattern this is meant to avoid, and under
  the DPDP Act consent has to be as easy to refuse as to give.
*/
export default function CookieBanner() {
  const [choice, setChoice] = useState(() => readConsent());
  const { pathname } = useLocation();

  // Another tab, or the "change your choice" button on the policy page.
  useEffect(() => {
    const onChange = (e) => setChoice(e.detail);
    window.addEventListener('cs-consent', onChange);
    return () => window.removeEventListener('cs-consent', onChange);
  }, []);

  const decide = (value) => {
    saveConsent(value);
    setChoice(value);

    // Only now does anything reach Google.
    if (value === GRANTED) initAnalytics();
  };

  // The admin panel is the shop owner's own tool, not a public page.
  if (choice !== null || pathname.startsWith('/admin')) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="cookie-banner"
        role="dialog"
        aria-live="polite"
        aria-label="Cookie choices"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="cookie-banner-text">
          <strong>A quick word about cookies</strong>
          <p>
            We'd like to use Google Analytics to see which stones people
            look at, so we know what to stock. It's entirely optional —
            the shop works exactly the same either way.{' '}
            <Link to="/cookie-policy">What we'd collect</Link>
          </p>
        </div>

        <div className="cookie-banner-actions">
          <button
            type="button"
            className="cookie-btn"
            onClick={() => decide(DENIED)}
          >
            Decline
          </button>
          <button
            type="button"
            className="cookie-btn"
            onClick={() => decide(GRANTED)}
          >
            Accept
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
